/* eslint-disable no-else-return */
import BigNumber from 'bignumber.js';

import { OrderSide, OrderType } from '#/api/orders';

import { ActiveAccountView } from '#/features/account/account-context';
import { logException } from '#/features/logging/logging';
import { MarketsStoreView } from '#/features/perpetuals/market-info/markets-context';
import { MarketsSummaryStoreView } from '#/features/perpetuals/market-info/markets-summary-context';
import {
  OpenPositionsStore,
  positionId,
} from '#/features/perpetuals/positions/open-positions-store-context';
import { OpenOrdersStoreView } from '#/features/perpetuals/trade-overview/open-orders-context';

import { brent } from './brent';
import {
  calculate_margin_requirement,
  SimplifiedOpenOrder,
} from './calculate_margin_requirement_simplified';

const MAX_ITERATIONS = 1000;
/**
 * Adapted from
 * @see https://github.com/tradeparadigm/quant/blob/5258bf788bc8647f8e9e93ba1d88006f2c082770/research_notebooks/order_slider_updated_poc.ipynb
 */
export default function get_order_size_from_free_collateral_usage(
  target_asset: string, // SyntheticAsset, // e.g. 'SOL-USD-PERP'
  order_side: OrderSide,
  limit_price: BigNumber,
  target_usage: BigNumber, // our leverage %
  openPositionsStore: OpenPositionsStore,
  openOrdersStore: OpenOrdersStoreView,
  marketsSummaryStore: MarketsSummaryStoreView,
  marketsStore: MarketsStoreView,
  activeAccountView: ActiveAccountView,
  profileMaxSlippage: BigNumber | null, // current market max slippage
  accountImfBase: BigNumber | null,
  order_type: OrderType = 'LIMIT', // Default to LIMIT if not provided
): BigNumber {
  const targetAssetMarket = marketsStore.getMarket(target_asset);
  const targetAssetMarketSummary =
    marketsSummaryStore.getMarketSummary(target_asset);

  if (targetAssetMarket == null) {
    throw new Error(
      `Market is not initialized for target_asset=${target_asset}`,
    );
  }
  if (targetAssetMarketSummary == null) {
    throw new Error(
      `Market summary is not initialized for target_asset=${target_asset}`,
    );
  }
  if (targetAssetMarketSummary.mark_price == null) {
    throw new Error(
      `'mark_price' is not initialized for ${target_asset} market summary`,
    );
  }
  if (activeAccountView.account == null) {
    throw new Error('Account is not initialized');
  }

  const position = openPositionsStore.get(
    positionId(target_asset, activeAccountView.account.account),
  );

  const current_free_collateral = activeAccountView.account.free_collateral;
  const extra_funds = target_usage.multipliedBy(current_free_collateral);

  if (extra_funds.eq(0)) {
    return new BigNumber(0);
  }

  const targetAssetOpenOrders = openOrdersStore
    .getAll()
    .filter(
      (order) =>
        order.market === target_asset &&
        order.account === activeAccountView.account!.account,
    );

  // Get current IMR
  const current_imr = calculate_margin_requirement(
    'Initial',
    position,
    targetAssetMarketSummary,
    targetAssetMarket,
    targetAssetOpenOrders,
    profileMaxSlippage,
    accountImfBase,
  );

  // Target IMR is current IMR plus the extra funds we want to use
  const target_imr = current_imr.plus(extra_funds);

  // Function that calculates IMR difference for a given order size
  const calculateImrDiffForOrderSize = (order_size: number): number => {
    const orderSizeBN = new BigNumber(order_size);

    // Create a new order with the given size
    const newOrder: SimplifiedOpenOrder = {
      side: order_side,
      size: orderSizeBN,
      type: order_type,
      price: limit_price,
      flags: [],
      remaining_size: orderSizeBN,
      status: 'OPEN',
      trigger_price: limit_price,
    };

    // Clone current orders and add new order
    const updatedOrders = [...targetAssetOpenOrders, newOrder];

    // Calculate IMR with the updated orders
    const imr_with_order = calculate_margin_requirement(
      'Initial',
      position,
      targetAssetMarketSummary,
      targetAssetMarket,
      updatedOrders,
      profileMaxSlippage,
      accountImfBase,
    );
    // Return difference between calculated IMR and target IMR
    return imr_with_order.minus(target_imr).toNumber();
  };

  try {
    // Use Brent's method to find the order size that gives us the target IMR
    // This is equivalent to your brentq function in Python
    const result = brent(
      calculateImrDiffForOrderSize,
      0, // lower bound
      Number.MAX_SAFE_INTEGER, // upper bound
      targetAssetMarket.price_tick_size.toNumber(), // tolerance
      MAX_ITERATIONS, // max iterations
    );

    return new BigNumber(result);
  } catch (error) {
    logException(error, {
      target_asset,
      order_side,
      limit_price: limit_price.toString(),
      target_usage: target_usage.toString(),
      current_free_collateral: current_free_collateral.toString(),
      extra_funds: extra_funds.toString(),
      current_imr: current_imr.toString(),
      target_imr: target_imr.toString(),
      order_type,
      profileMaxSlippage: profileMaxSlippage?.toString(),
      accountImfBase: accountImfBase?.toString(),
      position,
    });
    return new BigNumber(0);
  }
}
